/* LAB foundation behaviours. Dependency free, loaded with defer.
   1. Viewport-entry reveals. Content fails visible: CSS only hides a
      .lab-reveal element while it carries .lab-reveal--armed, and this
      script arms an element only once an IntersectionObserver is
      watching it. Automated browsers (screenshot captures render the
      full page without scrolling, so the observer never fires), no
      observer support and reduced motion all show everything
      immediately, and a safety timer reveals anything still armed so
      no failure mode can leave content hidden.
   2. Delegated clicks:
      [data-lab-drawer-open] dispatches 'lab:cart:open' on document
      (the cart drawer listens for it).
      [data-lab-menu-toggle] toggles the disclosure named by aria-controls.
   3. Escape closes any open [data-lab-dismissible].
   4. Shopify product-recommendations elements hydrate when they approach
      the viewport. The LAB layout intentionally does not load Dawn's large
      global.js bundle, so this small loader keeps its native recommendations
      endpoint working without pulling in unrelated Dawn behaviours. */

(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var REVEAL_SAFETY_MS = 5000;

  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i += 1) {
      nodes[i].classList.remove('lab-reveal--armed');
      nodes[i].classList.add('is-visible');
    }
  }

  /* Full-page captures paint below the fold without scrolling, so
     natively lazy images never load there. Real visitors keep lazy
     loading; this only runs for automated browsers. */
  function eagerLoadLazyImages() {
    var images = document.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < images.length; i += 1) {
      images[i].loading = 'eager';
    }
  }

  function initReveals() {
    var nodes = document.querySelectorAll('.lab-reveal:not(.is-visible)');

    if (navigator.webdriver) {
      eagerLoadLazyImages();
      revealAll(nodes);
      return;
    }

    if (!nodes.length) return;

    if (!('IntersectionObserver' in window) || reducedMotion.matches) {
      revealAll(nodes);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.remove('lab-reveal--armed');
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    for (var j = 0; j < nodes.length; j += 1) {
      nodes[j].classList.add('lab-reveal--armed');
      observer.observe(nodes[j]);
    }

    /* Fail visible: whatever the observer has not reached by now simply
       shows. Elements it has revealed are no longer armed, so this is a
       no-op on the happy path. */
    window.setTimeout(function () {
      revealAll(
        document.querySelectorAll('.lab-reveal.lab-reveal--armed:not(.is-visible)')
      );
    }, REVEAL_SAFETY_MS);
  }

  function setToggleState(toggle, expanded) {
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function dismiss(el) {
    var open = !el.hidden || el.classList.contains('is-open');
    if (!open) return;

    el.classList.remove('is-open');
    el.hidden = true;

    if (el.id) {
      var controls = document.querySelectorAll('[aria-controls="' + el.id + '"]');
      for (var i = 0; i < controls.length; i += 1) {
        setToggleState(controls[i], false);
      }
    }

    el.dispatchEvent(new CustomEvent('lab:dismiss', { bubbles: true }));
  }

  if (window.customElements && !window.customElements.get('product-recommendations')) {
    class LabProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.dataset.labRecommendationsInitialized === 'true') return;

        this.dataset.labRecommendationsInitialized = 'true';

        if (!('IntersectionObserver' in window)) {
          this.loadRecommendations();
          return;
        }

        this.recommendationsObserver = new IntersectionObserver(
          function (entries, observer) {
            if (!entries[0].isIntersecting) return;

            observer.disconnect();
            this.loadRecommendations();
          }.bind(this),
          { rootMargin: '0px 0px 500px 0px' }
        );
        this.recommendationsObserver.observe(this);
      }

      disconnectedCallback() {
        if (this.recommendationsObserver) this.recommendationsObserver.disconnect();
      }

      loadRecommendations() {
        var url = this.dataset.url;
        var productId = this.dataset.productId;
        var sectionId = this.dataset.sectionId;

        if (!url || !productId || !sectionId) return;

        var separator = url.indexOf('?') === -1 ? '?' : '&';
        var requestUrl =
          url +
          separator +
          'product_id=' +
          encodeURIComponent(productId) +
          '&section_id=' +
          encodeURIComponent(sectionId);

        this.setAttribute('aria-busy', 'true');

        fetch(requestUrl)
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Product recommendations request failed: ' + response.status);
            }

            return response.text();
          })
          .then(
            function (text) {
              var responseDocument = new DOMParser().parseFromString(text, 'text/html');
              var recommendations = responseDocument.querySelector('product-recommendations');

              if (recommendations && recommendations.innerHTML.trim()) {
                this.innerHTML = recommendations.innerHTML;
                this.classList.add('product-recommendations--loaded');
                initReveals();
              }

              this.removeAttribute('aria-busy');
              this.dispatchEvent(
                new CustomEvent('lab:recommendations:loaded', {
                  bubbles: true,
                  detail: { populated: Boolean(this.children.length) },
                })
              );
            }.bind(this)
          )
          .catch(
            function (error) {
              this.removeAttribute('aria-busy');
              this.dispatchEvent(
                new CustomEvent('lab:recommendations:error', {
                  bubbles: true,
                  detail: { error: error },
                })
              );
              console.error(error);
            }.bind(this)
          );
      }
    }

    window.customElements.define('product-recommendations', LabProductRecommendations);
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;

    var opener = event.target.closest('[data-lab-drawer-open]');
    if (opener) {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('lab:cart:open', { bubbles: true }));
      return;
    }

    var toggle = event.target.closest('[data-lab-menu-toggle]');
    if (toggle) {
      var targetId = toggle.getAttribute('aria-controls');
      var target = targetId ? document.getElementById(targetId) : null;
      var expanded = toggle.getAttribute('aria-expanded') === 'true';

      setToggleState(toggle, !expanded);
      if (target) {
        target.hidden = expanded;
        target.classList.toggle('is-open', !expanded);
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    var dismissibles = document.querySelectorAll('[data-lab-dismissible]');
    for (var i = 0; i < dismissibles.length; i += 1) {
      dismiss(dismissibles[i]);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveals);
  } else {
    initReveals();
  }

  /* Re-scan when the Theme Editor injects or reloads a section. */
  document.addEventListener('shopify:section:load', initReveals);
})();
